import type { PortfolioDetailResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	ClientPortfolioDomainEnum,
	PortfolioEventTypeEnum,
	ProductCategoryEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { resolveProductCategory } from "web_myplann/NotificationCenter/EventAlert/productCategory";
import { getTravelInsuranceDuration } from "web_myplann/NotificationCenter/EventAlert/travelInsuranceDuration";
import {
	EventClassificationEnum,
	type EventVariant,
} from "web_myplann/NotificationCenter/EventAlert/types";

/** End / fixation-end alerts are orange ("!"), anniversaries are blue ("i"). */
export const getEventVariant = (
	eventType: PortfolioEventTypeEnum,
): EventVariant =>
	eventType === PortfolioEventTypeEnum.Anniversary ? "info" : "warning";

/** Domains whose classification is unambiguous from (domain, eventType) alone. */
const unambiguousClassification: Partial<
	Record<
		ClientPortfolioDomainEnum,
		Partial<Record<PortfolioEventTypeEnum, EventClassificationEnum>>
	>
> = {
	[ClientPortfolioDomainEnum.LifeInsurance]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.LifeInsuranceEnd,
		[PortfolioEventTypeEnum.Anniversary]:
			EventClassificationEnum.LifeInsuranceAnniversary,
	},
	[ClientPortfolioDomainEnum.PensionProducts]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.PensionEnd,
	},
	[ClientPortfolioDomainEnum.BuildingSavings]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.BuildingSavingsEnd,
	},
	[ClientPortfolioDomainEnum.Investments]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.InvestmentEnd,
	},
	[ClientPortfolioDomainEnum.BankingProducts]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.BankProductEnd,
	},
	[ClientPortfolioDomainEnum.BusinessLoans]: {
		[PortfolioEventTypeEnum.FixationEnd]:
			EventClassificationEnum.BusinessLoanFixationEnd,
	},
	[ClientPortfolioDomainEnum.Commodities]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.CommodityEnd,
	},
	[ClientPortfolioDomainEnum.Services]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.ServiceEnd,
		[PortfolioEventTypeEnum.Anniversary]:
			EventClassificationEnum.ServiceAnniversary,
	},
	[ClientPortfolioDomainEnum.Leasing]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.LeasingEnd,
	},
	[ClientPortfolioDomainEnum.Cryptocurrencies]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.CryptocurrencyEnd,
	},
};

/**
 * Classification for the product categories the removed backend resolver singled
 * out for a fine-grained row, keyed by event type.
 *
 * The event-type dimension is load-bearing: a mortgage carries both a contract
 * end (`endAt`) and a fixation end (`fixationEndDate`), and the backend emits an
 * alert for each. Without this dimension a contract `End` rendered the fixation
 * copy — a contract running out for good was shown as "your fixation expires".
 *
 * A category present here is authoritative: an event type it does not list
 * resolves to null rather than falling through to the (domain, eventType) map.
 * Travel insurance additionally depends on the contract duration, so it is
 * handled separately (see `travelInsuranceClassification`).
 */
const productCategoryClassification: Partial<
	Record<
		ProductCategoryEnum,
		Partial<Record<PortfolioEventTypeEnum, EventClassificationEnum>>
	>
> = {
	[ProductCategoryEnum.VehicleInsurance]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.CarInsuranceEnd,
		[PortfolioEventTypeEnum.Anniversary]:
			EventClassificationEnum.CarInsuranceAnniversary,
	},
	[ProductCategoryEnum.PropertyAndLiabilityInsurance]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.PropertyInsuranceEnd,
		[PortfolioEventTypeEnum.Anniversary]:
			EventClassificationEnum.PropertyInsuranceAnniversary,
	},
	[ProductCategoryEnum.RealEstate]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.RealEstateEnd,
	},
	[ProductCategoryEnum.HousingLoans]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.HousingLoanEnd,
		[PortfolioEventTypeEnum.FixationEnd]:
			EventClassificationEnum.HousingLoanFixationEnd,
	},
	[ProductCategoryEnum.ConsumerLoans]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.ConsumerLoanEnd,
		[PortfolioEventTypeEnum.FixationEnd]:
			EventClassificationEnum.ConsumerLoanFixationEnd,
	},
};

const travelInsuranceClassification: Record<
	"short" | "long",
	EventClassificationEnum
> = {
	short: EventClassificationEnum.TravelInsuranceShortEnd,
	long: EventClassificationEnum.TravelInsuranceLongEnd,
};

/** The subset of the portfolio DTO needed to resolve the fine-grained product category. */
type PortfolioClassificationInput = Pick<
	PortfolioDetailResponseDTO,
	"productCategoryId" | "startAt" | "endAt"
>;

/**
 * Maps a backend alert to the spec-table row used for its title/text.
 *
 * Resolution order:
 * 1. Travel insurance — the row also depends on the contract duration.
 * 2. The `productCategoryId` table, which is authoritative when the category is
 *    listed: it is keyed by event type, and an unlisted event type resolves to
 *    null rather than falling through. This covers the cases the domain alone
 *    cannot tell apart (car vs. property, real estate, housing vs. consumer
 *    loan, contract end vs. fixation end) and replicates the resolver
 *    previously computed on the backend.
 * 3. Non-life insurance with no fine-grained category — the "other"
 *    accident/health row.
 * 4. Everything else, keyed by (domain, eventType).
 *
 * Returns null for an unexpected combination so the caller can skip it safely.
 */
export const resolveClassification = (
	domain: ClientPortfolioDomainEnum | null | undefined,
	eventType: PortfolioEventTypeEnum,
	portfolio?: PortfolioClassificationInput | null,
): EventClassificationEnum | null => {
	const productCategory = resolveProductCategory(portfolio?.productCategoryId);
	const isAnniversary = eventType === PortfolioEventTypeEnum.Anniversary;

	if (productCategory === ProductCategoryEnum.TravelInsurance) {
		if (isAnniversary) {
			return null;
		}
		const duration = getTravelInsuranceDuration(
			portfolio?.startAt,
			portfolio?.endAt,
		);
		return travelInsuranceClassification[duration];
	}

	const categoryClassification = productCategory
		? productCategoryClassification[productCategory]
		: undefined;
	if (categoryClassification) {
		return categoryClassification[eventType] ?? null;
	}

	if (domain === ClientPortfolioDomainEnum.PropertyInsurance) {
		return isAnniversary
			? EventClassificationEnum.AccidentHealthInsuranceAnniversary
			: EventClassificationEnum.AccidentHealthInsuranceEnd;
	}

	// Loans (the generic parent — housing/consumer arrive via productCategoryId)
	// and PaidAdvisory have no alert row, so they intentionally fall through to null.
	return domain
		? (unambiguousClassification[domain]?.[eventType] ?? null)
		: null;
};
